import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { useAuth } from "../../services/AuthContext";
import { normalizeLanguage } from "../../services/language";
import ModernSidebar, { useModernRouteActions } from "../../components/lobby/ModernSidebar";
import { learnCompleteNavigationGroups } from "../../content/learnPages";
import { getLearnGuideSearchResults, getLearnGuideSearchTokens } from "../../utils/learnGuideSearch";
import { resolveLearnPathForMode } from "../../utils/learnRoutes";
import {
  SUPPORT_CATEGORIES,
  SUPPORT_EMAIL,
  SUPPORT_FAQ,
  SUPPORT_QUICK_REPLIES,
  SUPPORT_REQUEST_CATEGORIES,
  SUPPORT_WHATSAPP,
} from "./supportFaq";
import "../../components/lobby/lobbyModern.css";
import "./supportV2.css";

// Centro de ayuda (diseño 3): buscador, temas, preguntas frecuentes, «Mis solicitudes»
// y contacto directo. Los administradores tienen además la «Bandeja de soporte».

const MAX_MESSAGE_LENGTH = 1000;
const POLL_MS = 20000;
const CONTACT_STORAGE_KEY = "xima_support_contact_v1";
const STATUS_ORDER = ["new", "review", "replied", "closed"];
const STATUS_LABELS = {
  es: { new: "Pendiente", review: "En revisión", replied: "Respondida", closed: "Cerrada" },
  en: { new: "Pending", review: "In review", replied: "Answered", closed: "Closed" },
};
const STATUS_TONES = { new: "red", review: "gold", replied: "green", closed: "cyan" };

const categoryOf = (id) =>
  SUPPORT_REQUEST_CATEGORIES.find((entry) => entry.id === id) ||
  SUPPORT_REQUEST_CATEGORIES[SUPPORT_REQUEST_CATEGORIES.length - 1];

const toneOf = (value) => {
  const tones = ["is-blue", "is-purple", "is-teal", "is-orange", "is-pink", "is-green"];
  const text = String(value || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  return tones[hash % tones.length];
};

const initialOf = (name) => String(name || "?").trim().charAt(0).toUpperCase() || "?";

const errorText = (error, fallback) => error?.response?.data?.message || fallback;

const readStoredContact = () => {
  try {
    return window.localStorage.getItem(CONTACT_STORAGE_KEY) || "";
  } catch (error) {
    return "";
  }
};

const storeContact = (value) => {
  try {
    window.localStorage.setItem(CONTACT_STORAGE_KEY, value);
  } catch (error) {
    // Sin almacenamiento: solo no se recuerda el contacto.
  }
};

const formatDateTime = (value, language) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const locale = language === "es" ? "es-ES" : "en-US";
  const weekday = date.toLocaleDateString(locale, { weekday: "long" });
  const day = date.toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" });
  const time = date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${weekday} ${day} · ${time}`;
};

const formatAgo = (value, language) => {
  const ms = Date.now() - new Date(value).getTime();
  const es = language === "es";
  if (!Number.isFinite(ms) || ms < 0) return "";
  const m = Math.floor(ms / 60000);
  if (m < 1) return es ? "ahora" : "now";
  if (m < 60) return es ? `hace ${m} min` : `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return es ? `hace ${h} h` : `${h} h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return es ? `hace ${d} ${d === 1 ? "día" : "días"}` : `${d}d ago`;
  return new Date(value).toLocaleDateString(es ? "es-ES" : "en-US", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const contextLabel = (ticket, t) => {
  const parts = [];
  const page = String(ticket?.page || "").trim();
  if (page && page !== "support") parts.push(page.charAt(0).toUpperCase() + page.slice(1));
  const mode = String(ticket?.mode || "").trim();
  if (mode) parts.push(mode.charAt(0).toUpperCase() + mode.slice(1));
  if (ticket?.roomId) parts.push(`${t("Sala", "Room")} ${ticket.roomId}`);
  return parts.join(" · ");
};

const StatusPill = ({ status, language }) => (
  <span className={`xsu-pill is-${STATUS_TONES[status] || "cyan"}`}>
    {(STATUS_LABELS[language] || STATUS_LABELS.en)[status] || status}
  </span>
);

const Donate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const outletContext = useOutletContext() || {};
  const routeActions = useModernRouteActions();
  const { user } = useAuth();
  const language = normalizeLanguage(outletContext.language || "es");
  const lang = language === "es" ? "es" : "en";
  const t = useCallback((es, en) => (lang === "es" ? es : en), [lang]);
  const refreshSupportAlert = outletContext.refreshSupportAlert;

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const contextRoomId = String(searchParams.get("roomId") || "").trim();
  const contextMode = String(searchParams.get("mode") || "").trim();

  const [tickets, setTickets] = useState([]);
  const [serverIsAdmin, setServerIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [openFaq, setOpenFaq] = useState("");
  const [view, setView] = useState("help");
  const [composer, setComposer] = useState(null);
  const [openTicketId, setOpenTicketId] = useState("");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");

  const isAdmin = serverIsAdmin || Boolean(outletContext.hasAdminMenuAccess);
  const myUserId = String(user?.loginIdentifier || user?.email || "").trim().toLowerCase();

  useEffect(() => {
    document.body.classList.add("xima-lobby-modern-active", "xsu-page-active");
    return () => document.body.classList.remove("xima-lobby-modern-active", "xsu-page-active");
  }, []);

  const loadTickets = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await axios.get("/support/tickets");
      setTickets(Array.isArray(response?.data?.tickets) ? response.data.tickets : []);
      setServerIsAdmin(response?.data?.isAdmin === true);
      setLoadError("");
    } catch (error) {
      if (!silent) setLoadError(errorText(error, t("No se pudieron cargar las solicitudes.", "Could not load requests.")));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadTickets();
    const id = window.setInterval(() => loadTickets({ silent: true }), POLL_MS);
    return () => window.clearInterval(id);
  }, [loadTickets]);

  // El administrador empieza en su bandeja.
  const adminViewChosenRef = useRef(false);
  useEffect(() => {
    if (adminViewChosenRef.current || !isAdmin) return;
    adminViewChosenRef.current = true;
    setView(searchParams.get("vista") === "ayuda" ? "help" : "inbox");
  }, [isAdmin, searchParams]);

  const myTickets = useMemo(
    () => tickets.filter((ticket) => !isAdmin || ticket.userId === myUserId),
    [isAdmin, myUserId, tickets]
  );
  const unreadMine = myTickets.filter((ticket) => ticket.unreadForUser).length;

  // Al ver sus solicitudes, el jugador deja de tener respuestas sin leer.
  useEffect(() => {
    if (loading || view !== "help" || unreadMine === 0) return;
    axios
      .post("/support/tickets/seen")
      .then(() => refreshSupportAlert?.())
      .catch(() => {});
  }, [loading, refreshSupportAlert, unreadMine, view]);

  useEffect(() => {
    if (!notice) return undefined;
    const id = window.setTimeout(() => setNotice(""), 3500);
    return () => window.clearTimeout(id);
  }, [notice]);

  const replaceTicket = useCallback((ticket) => {
    if (!ticket?.ticketId) return;
    setTickets((previous) => {
      const rest = previous.filter((entry) => entry.ticketId !== ticket.ticketId);
      const kept = previous.find((entry) => entry.ticketId === ticket.ticketId);
      return [{ ...kept, ...ticket, playerCode: ticket.playerCode || kept?.playerCode || "" }, ...rest];
    });
  }, []);

  // ---- Buscador y preguntas frecuentes ----
  const faqText = useCallback((item) => item[lang] || item.es, [lang]);
  const faqSearchGroups = useMemo(
    () => [
      {
        label: "faq",
        items: SUPPORT_FAQ.map((item) => ({
          ...item,
          label: faqText(item).q,
          searchPrimaryText: `${faqText(item).q} ${item.es.q}`,
          searchSecondaryText: `${faqText(item).a} ${item.es.a}`,
        })),
      },
    ],
    [faqText]
  );
  const isSearching = getLearnGuideSearchTokens(query).length > 0;
  const faqResults = useMemo(
    () => (isSearching ? getLearnGuideSearchResults(faqSearchGroups, query, { limit: 8 }) : []),
    [faqSearchGroups, isSearching, query]
  );
  const guideResults = useMemo(
    () => (isSearching ? getLearnGuideSearchResults(learnCompleteNavigationGroups, query, { limit: 5 }) : []),
    [isSearching, query]
  );
  const visibleFaq = useMemo(() => {
    if (isSearching) return faqResults;
    if (activeCategory) return SUPPORT_FAQ.filter((item) => item.category === activeCategory);
    return SUPPORT_FAQ.filter((item) => item.popular);
  }, [activeCategory, faqResults, isSearching]);

  const openComposer = (category = "") => {
    setOpenTicketId("");
    const accountEmail = String(user?.contactEmail || user?.email || "").trim();
    const usableEmail =
      accountEmail.includes("@") && !/pendiente|archivada|guest|invitado/i.test(accountEmail) ? accountEmail : "";
    setComposer({
      category: category || activeCategory || "",
      contact: readStoredContact() || usableEmail,
      message: "",
      error: "",
    });
  };

  const runFaqAction = (item) => {
    if (item.request) {
      openComposer(item.request);
      return;
    }
    if (item.to) navigate(item.to);
  };

  const pickCategory = (id) => {
    setQuery("");
    setOpenFaq("");
    setActiveCategory((previous) => (previous === id ? "" : id));
  };

  // ---- Crear solicitud ----
  const submitTicket = async () => {
    if (!composer) return;
    const message = composer.message.trim();
    const contact = composer.contact.trim();
    if (!composer.category) {
      setComposer({ ...composer, error: t("Elige de qué trata tu solicitud.", "Choose what your request is about.") });
      return;
    }
    if (!contact && !isAdmin) {
      setComposer({ ...composer, error: t("Escribe un teléfono o correo para poder contestarte.", "Enter a phone or email so we can answer you.") });
      return;
    }
    if (!message) {
      setComposer({ ...composer, error: t("Escribe tu mensaje.", "Write your message.") });
      return;
    }
    setBusy("create");
    try {
      const response = await axios.post("/support/tickets", {
        category: composer.category,
        contact,
        message,
        page: contextRoomId ? "game" : "support",
        roomId: contextRoomId,
        mode: contextMode,
      });
      if (contact) storeContact(contact);
      const ticket = response?.data?.ticket;
      if (ticket) replaceTicket(ticket);
      setComposer(null);
      setNotice(
        ticket?.number
          ? t(`Solicitud #${ticket.number} enviada. Te responderemos aquí.`, `Request #${ticket.number} sent. We'll answer here.`)
          : t("Solicitud enviada.", "Request sent.")
      );
      if (ticket?.ticketId) setOpenTicketId(ticket.ticketId);
      refreshSupportAlert?.();
    } catch (error) {
      setComposer((previous) => previous && { ...previous, error: errorText(error, t("No se pudo enviar la solicitud.", "Could not send the request.")) });
    } finally {
      setBusy("");
    }
  };

  // ---- Acciones sobre una solicitud ----
  const sendReply = async (ticketId, text) => {
    const message = String(text || "").trim();
    if (!message) return false;
    setBusy(`reply:${ticketId}`);
    try {
      const response = await axios.post(`/support/tickets/${encodeURIComponent(ticketId)}/messages`, { message });
      replaceTicket(response?.data?.ticket);
      refreshSupportAlert?.();
      return true;
    } catch (error) {
      setNotice(errorText(error, t("No se pudo enviar el mensaje.", "Could not send the message.")));
      return false;
    } finally {
      setBusy("");
    }
  };

  const setStatus = async (ticketId, status) => {
    setBusy(`status:${ticketId}`);
    try {
      const response = await axios.patch(`/support/tickets/${encodeURIComponent(ticketId)}`, { status });
      replaceTicket(response?.data?.ticket);
      refreshSupportAlert?.();
    } catch (error) {
      setNotice(errorText(error, t("No se pudo cambiar el estado.", "Could not change the status.")));
    } finally {
      setBusy("");
    }
  };

  const deleteTicket = async (ticket) => {
    if (!window.confirm(t(`¿Borrar la solicitud #${ticket.number} de ${ticket.userName}?`, `Delete request #${ticket.number} from ${ticket.userName}?`))) return;
    setBusy(`delete:${ticket.ticketId}`);
    try {
      await axios.delete(`/support/tickets/${encodeURIComponent(ticket.ticketId)}`);
      setTickets((previous) => previous.filter((entry) => entry.ticketId !== ticket.ticketId));
      setOpenTicketId("");
      refreshSupportAlert?.();
    } catch (error) {
      setNotice(errorText(error, t("No se pudo borrar la solicitud.", "Could not delete the request.")));
    } finally {
      setBusy("");
    }
  };

  const deleteMessage = async (ticketId, messageId) => {
    if (!window.confirm(t("¿Borrar este mensaje?", "Delete this message?"))) return;
    try {
      await axios.delete(`/support/messages/${encodeURIComponent(messageId)}`);
      setTickets((previous) =>
        previous.map((entry) =>
          entry.ticketId === ticketId
            ? { ...entry, messages: entry.messages.filter((m) => m.id !== messageId) }
            : entry
        )
      );
    } catch (error) {
      setNotice(errorText(error, t("No se pudo borrar el mensaje.", "Could not delete the message.")));
    }
  };

  const clearAll = async () => {
    if (!window.confirm(t("¿Borrar TODOS los mensajes de soporte? No se puede deshacer.", "Delete ALL support messages? This can't be undone."))) return;
    setBusy("clear");
    try {
      await axios.delete("/support/messages");
      setTickets([]);
      setOpenTicketId("");
      refreshSupportAlert?.();
    } catch (error) {
      setNotice(errorText(error, t("No se pudieron borrar los mensajes.", "Could not delete the messages.")));
    } finally {
      setBusy("");
    }
  };

  const pendingCount = tickets.filter((ticket) => ticket.status === "new").length;

  return (
    <div className="xlm-root xsu-root">
      <ModernSidebar
        language={language}
        active="support"
        badges={outletContext.sidebarBadges || {}}
        isAdmin={Boolean(outletContext.hasAdminMenuAccess)}
        actions={routeActions}
      />
      <div className="xlm-main xsu-main">
        <div className="xsu-shell">
          <button type="button" className="xsu-back" onClick={() => navigate("/game/lobby")}>
            ‹ {t("Volver al lobby", "Back to lobby")}
          </button>
          <header className="xsu-head">
            <div className="xsu-ico" aria-hidden="true">{view === "inbox" ? "🛡️" : "🛟"}</div>
            <div className="xsu-head-text">
              <h1>{view === "inbox" ? t("Bandeja de soporte", "Support inbox") : t("Centro de ayuda", "Help center")}</h1>
              <p>
                {view === "inbox"
                  ? t("Todas las solicitudes de los jugadores, ordenadas por conversación.", "Every player request, grouped by conversation.")
                  : t("Respuestas rápidas, tus solicitudes y contacto directo con el equipo Xima.", "Quick answers, your requests and direct contact with the Xima team.")}
              </p>
            </div>
            {isAdmin ? (
              <div className="xsu-switch" role="tablist">
                <button type="button" className={view === "help" ? "is-on" : ""} onClick={() => setView("help")}>
                  🛟 {t("Centro de ayuda", "Help center")}
                </button>
                <button type="button" className={view === "inbox" ? "is-on" : ""} onClick={() => setView("inbox")}>
                  🛡️ {t("Bandeja", "Inbox")}
                  {pendingCount > 0 ? <span className="xsu-count">{pendingCount}</span> : null}
                </button>
              </div>
            ) : null}
          </header>

          {notice ? <div className="xsu-notice">{notice}</div> : null}

          {view === "inbox" && isAdmin ? (
            <AdminInbox
              t={t}
              lang={lang}
              tickets={tickets}
              loading={loading}
              loadError={loadError}
              busy={busy}
              onReply={sendReply}
              onStatus={setStatus}
              onDeleteTicket={deleteTicket}
              onDeleteMessage={deleteMessage}
              onClearAll={clearAll}
              onHistory={(ticket) =>
                navigate(`/game/history?jugador=${encodeURIComponent(ticket.playerCode || ticket.userId)}`)
              }
            />
          ) : (
            <>
              <section className="xsu-hero">
                <h2>{t("¿En qué podemos ayudarte?", "How can we help you?")}</h2>
                <label className="xsu-search">
                  <span aria-hidden="true">🔍</span>
                  <input
                    type="search"
                    value={query}
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setActiveCategory("");
                    }}
                    placeholder={t("Busca: «monedas», «contraseña», «Premium», «torneo»…", "Search: «coins», «password», «Premium», «tournament»…")}
                    aria-label={t("Buscar ayuda", "Search help")}
                  />
                  {query ? (
                    <button type="button" className="xsu-search-clear" onClick={() => setQuery("")} aria-label={t("Borrar búsqueda", "Clear search")}>
                      ✕
                    </button>
                  ) : null}
                </label>
              </section>

              <div className="xsu-grid">
                <div className="xsu-col">
                  <div className="xsu-cats">
                    {SUPPORT_CATEGORIES.map((category) => {
                      const [title, sub] = category[lang] || category.es;
                      return (
                        <button
                          type="button"
                          key={category.id}
                          className={`xsu-cat is-${category.tone}${activeCategory === category.id ? " is-active" : ""}`}
                          onClick={() => pickCategory(category.id)}
                          aria-pressed={activeCategory === category.id}
                        >
                          <span className="xsu-cat-ico" aria-hidden="true">{category.icon}</span>
                          <span className="xsu-cat-text">
                            <b>{title}</b>
                            <span>{sub}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <section className="xsu-card xsu-faq">
                    <div className="xsu-card-head">
                      <h3>
                        {isSearching
                          ? t(`Resultados para «${query.trim()}»`, `Results for «${query.trim()}»`)
                          : activeCategory
                            ? `${categoryOf(activeCategory).icon} ${(categoryOf(activeCategory)[lang] || categoryOf(activeCategory).es)[0]}`
                            : `⭐ ${t("Preguntas frecuentes", "Frequently asked questions")}`}
                      </h3>
                      {activeCategory || isSearching ? (
                        <button type="button" className="xsu-link" onClick={() => { setActiveCategory(""); setQuery(""); }}>
                          {t("Ver las más frecuentes", "Show most frequent")}
                        </button>
                      ) : null}
                    </div>

                    {visibleFaq.length === 0 && isSearching && guideResults.length === 0 ? (
                      <div className="xsu-empty">
                        <p>{t("No encontramos nada con esas palabras.", "We found nothing with those words.")}</p>
                        <button type="button" className="xsu-btn is-go" onClick={() => openComposer()}>
                          ＋ {t("Crear una solicitud", "Create a request")}
                        </button>
                      </div>
                    ) : null}

                    <ul className="xsu-faq-list">
                      {visibleFaq.map((item) => {
                        const text = faqText(item);
                        const isOpen = openFaq === item.id;
                        return (
                          <li key={item.id} className={isOpen ? "is-open" : ""}>
                            <button
                              type="button"
                              className="xsu-q"
                              onClick={() => setOpenFaq(isOpen ? "" : item.id)}
                              aria-expanded={isOpen}
                            >
                              <span>{text.q}</span>
                              <span className="xsu-q-arrow" aria-hidden="true">{isOpen ? "▾" : "▸"}</span>
                            </button>
                            {isOpen ? (
                              <div className="xsu-a">
                                <p>{text.a}</p>
                                {text.action ? (
                                  <button type="button" className="xsu-btn is-small" onClick={() => runFaqAction(item)}>
                                    {text.action} →
                                  </button>
                                ) : null}
                              </div>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>

                    {isSearching && guideResults.length > 0 ? (
                      <div className="xsu-guides">
                        <h4>📖 {t("Guías de Aprender", "Learn guides")}</h4>
                        {guideResults.map((item) => (
                          <button
                            type="button"
                            key={item.path}
                            className="xsu-guide"
                            onClick={() => navigate(resolveLearnPathForMode(item.path, false))}
                          >
                            <span>{item.label}</span>
                            <span aria-hidden="true">→</span>
                          </button>
                        ))}
                      </div>
                    ) : null}

                    {!isSearching ? (
                      <div className="xsu-faq-foot">
                        <span>{t("¿No encuentras lo que buscas?", "Can't find what you need?")}</span>
                        <button type="button" className="xsu-link" onClick={() => openComposer()}>
                          {t("Escríbenos", "Write to us")} →
                        </button>
                      </div>
                    ) : null}
                  </section>
                </div>

                <div className="xsu-col xsu-side">
                  <section className="xsu-card">
                    <div className="xsu-card-head">
                      <h3>🎫 {t("Mis solicitudes", "My requests")}</h3>
                      <button type="button" className="xsu-btn is-go is-small" onClick={() => openComposer()}>
                        ＋ {t("Nueva solicitud", "New request")}
                      </button>
                    </div>
                    {loading ? <p className="xsu-mute">{t("Cargando…", "Loading…")}</p> : null}
                    {loadError ? <p className="xsu-error">{loadError}</p> : null}
                    {!loading && !loadError && myTickets.length === 0 ? (
                      <p className="xsu-mute xsu-tickets-empty">
                        {t("Aún no has enviado ninguna solicitud. Si algo no funciona, pulsa «Nueva solicitud» y te respondemos aquí.", "You haven't sent any requests yet. If something isn't working, press «New request» and we'll answer here.")}
                      </p>
                    ) : null}
                    <div className="xsu-tickets">
                      {myTickets.map((ticket) => {
                        const last = ticket.messages[ticket.messages.length - 1];
                        const category = categoryOf(ticket.category);
                        return (
                          <button
                            type="button"
                            key={ticket.ticketId}
                            className={`xsu-ticket${ticket.unreadForUser ? " is-unread" : ""}`}
                            onClick={() => { setComposer(null); setOpenTicketId(ticket.ticketId); }}
                          >
                            <span className="xsu-ticket-top">
                              <b>#{ticket.number} · {(category[lang] || category.es)[0]}</b>
                              <StatusPill status={ticket.status} language={lang} />
                            </span>
                            <span className="xsu-ticket-sub">
                              {last ? `${last.isAdmin ? `${last.userName} (Admin): ` : ""}«${last.text.slice(0, 70)}${last.text.length > 70 ? "…" : ""}»` : ""}
                              {" · "}
                              {formatAgo(ticket.lastActivityAt, lang)}
                            </span>
                            {ticket.unreadForUser ? <span className="xsu-new-dot">{t("Respuesta nueva", "New reply")}</span> : null}
                          </button>
                        );
                      })}
                    </div>
                  </section>

                  <section className="xsu-card">
                    <h3>☎️ {t("Contacto directo", "Direct contact")}</h3>
                    {SUPPORT_WHATSAPP.map((phone) => (
                      <a
                        key={phone}
                        className="xsu-channel"
                        href={`https://wa.me/${phone.replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="xsu-channel-ico is-green" aria-hidden="true">💬</span>
                        <span>WhatsApp</span>
                        <b>{phone}</b>
                      </a>
                    ))}
                    <a className="xsu-channel" href={`mailto:${SUPPORT_EMAIL}`}>
                      <span className="xsu-channel-ico is-cyan" aria-hidden="true">✉️</span>
                      <span>{t("Correo", "Email")}</span>
                      <b className="xsu-mail">{SUPPORT_EMAIL}</b>
                    </a>
                    <p className="xsu-mute xsu-sla">⏱ {t("Solemos responder en menos de 24 horas.", "We usually answer within 24 hours.")}</p>
                  </section>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {composer ? (
        <div className="xsu-modal-back" onClick={() => setComposer(null)} role="presentation">
          <div className="xsu-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className="xsu-card-head">
              <h3>📨 {t("Nueva solicitud", "New request")}</h3>
              <button type="button" className="xsu-x" onClick={() => setComposer(null)} aria-label={t("Cerrar", "Close")}>✕</button>
            </div>
            <div className="xsu-label">{t("¿Sobre qué es?", "What is it about?")}</div>
            <div className="xsu-chips">
              {SUPPORT_REQUEST_CATEGORIES.map((category) => (
                <button
                  type="button"
                  key={category.id}
                  className={`xsu-chip${composer.category === category.id ? " is-on" : ""}`}
                  onClick={() => setComposer({ ...composer, category: category.id, error: "" })}
                >
                  {category.icon} {(category[lang] || category.es)[0]}
                </button>
              ))}
            </div>
            <label className="xsu-label" htmlFor="xsu-contact">{t("Teléfono o correo", "Phone or email")}</label>
            <input
              id="xsu-contact"
              className="xsu-input"
              value={composer.contact}
              maxLength={120}
              onChange={(event) => setComposer({ ...composer, contact: event.target.value, error: "" })}
              placeholder={t("Para poder contestarte también fuera de Xima", "So we can also answer you outside Xima")}
            />
            <label className="xsu-label" htmlFor="xsu-message">{t("Mensaje", "Message")}</label>
            <textarea
              id="xsu-message"
              className="xsu-input xsu-textarea"
              value={composer.message}
              maxLength={MAX_MESSAGE_LENGTH}
              onChange={(event) => setComposer({ ...composer, message: event.target.value, error: "" })}
              placeholder={t("Cuéntanos qué pasó. Si fue en una partida, dinos el modo, la sala o el torneo.", "Tell us what happened. If it was in a game, tell us the mode, room or tournament.")}
            />
            {contextRoomId ? (
              <p className="xsu-mute">{t(`Se adjunta la sala ${contextRoomId}.`, `Room ${contextRoomId} is attached.`)}</p>
            ) : null}
            {composer.error ? <p className="xsu-error">{composer.error}</p> : null}
            <div className="xsu-modal-foot">
              <span className={`xsu-mute${composer.message.length > MAX_MESSAGE_LENGTH - 100 ? " is-warn" : ""}`}>
                {composer.message.length}/{MAX_MESSAGE_LENGTH}
              </span>
              <button type="button" className="xsu-btn is-go" disabled={busy === "create"} onClick={submitTicket}>
                {busy === "create" ? t("Enviando…", "Sending…") : `${t("Enviar solicitud", "Send request")} ➤`}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {openTicketId && view === "help" ? (
        <TicketModal
          t={t}
          lang={lang}
          ticket={tickets.find((entry) => entry.ticketId === openTicketId)}
          busy={busy}
          onClose={() => setOpenTicketId("")}
          onReply={sendReply}
          onCloseTicket={(ticketId) => setStatus(ticketId, "closed")}
        />
      ) : null}
    </div>
  );
};

const Conversation = ({ ticket, lang, t, canDelete, onDeleteMessage, mineIsAdmin }) => {
  const endRef = useRef(null);
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: "end" });
  }, [ticket?.messages?.length]);
  return (
    <div className="xsu-msgs">
      {(ticket?.messages || []).map((message) => {
        const fromTeam = message.isAdmin;
        const isMine = mineIsAdmin ? fromTeam : !fromTeam;
        return (
          <div key={message.id} className={`xsu-msg${isMine ? " is-mine" : ""}${fromTeam ? " is-team" : ""}`}>
            <div className="xsu-msg-name">
              {fromTeam ? `${message.userName} (${t("Equipo Xima", "Xima team")})` : message.userName}
            </div>
            <div className="xsu-msg-text">{message.text}</div>
            <div className="xsu-msg-meta">
              <span>{formatDateTime(message.createdAt, lang)}</span>
              {canDelete && message.id !== ticket.ticketId ? (
                <button type="button" className="xsu-msg-del" onClick={() => onDeleteMessage(ticket.ticketId, message.id)}>
                  {t("Borrar", "Delete")}
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
};

const ReplyBox = ({ t, lang, ticketId, busy, onReply, placeholder, quickReplies }) => {
  const [text, setText] = useState("");
  useEffect(() => setText(""), [ticketId]);
  const sending = busy === `reply:${ticketId}`;
  const send = async () => {
    if (await onReply(ticketId, text)) setText("");
  };
  return (
    <div className="xsu-reply">
      {quickReplies ? (
        <div className="xsu-quick">
          {(SUPPORT_QUICK_REPLIES[lang] || SUPPORT_QUICK_REPLIES.es).map((reply) => (
            <button type="button" key={reply} className="xsu-chip is-quick" onClick={() => setText(reply)}>
              ⚡ {reply}
            </button>
          ))}
        </div>
      ) : null}
      <div className="xsu-reply-row">
        <textarea
          className="xsu-input xsu-reply-input"
          value={text}
          maxLength={MAX_MESSAGE_LENGTH}
          rows={2}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              send();
            }
          }}
          placeholder={placeholder}
        />
        <button type="button" className="xsu-btn is-go" disabled={sending || !text.trim()} onClick={send}>
          {sending ? "…" : `${t("Enviar", "Send")} ➤`}
        </button>
      </div>
    </div>
  );
};

const TicketModal = ({ t, lang, ticket, busy, onClose, onReply, onCloseTicket }) => {
  if (!ticket) return null;
  const category = categoryOf(ticket.category);
  return (
    <div className="xsu-modal-back" onClick={onClose} role="presentation">
      <div className="xsu-modal xsu-ticket-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <div className="xsu-card-head">
          <h3>
            #{ticket.number} · {category.icon} {(category[lang] || category.es)[0]}
          </h3>
          <StatusPill status={ticket.status} language={lang} />
          <button type="button" className="xsu-x" onClick={onClose} aria-label={t("Cerrar", "Close")}>✕</button>
        </div>
        <p className="xsu-mute">
          {t("Enviada el", "Sent on")} {formatDateTime(ticket.createdAt, lang)}
          {ticket.contact ? ` · ${t("Contacto", "Contact")}: ${ticket.contact}` : ""}
        </p>
        <Conversation ticket={ticket} lang={lang} t={t} />
        {ticket.status === "closed" ? (
          <p className="xsu-mute xsu-closed-note">
            {t("Esta solicitud está cerrada. Si nos escribes de nuevo aquí, se vuelve a abrir.", "This request is closed. If you write here again, it reopens.")}
          </p>
        ) : null}
        <ReplyBox
          t={t}
          lang={lang}
          ticketId={ticket.ticketId}
          busy={busy}
          onReply={onReply}
          placeholder={t("Añade algo más a tu solicitud…", "Add something to your request…")}
        />
        {ticket.status !== "closed" ? (
          <div className="xsu-modal-foot">
            <span className="xsu-mute">{t("¿Ya está resuelto?", "All sorted?")}</span>
            <button type="button" className="xsu-btn is-small" disabled={busy === `status:${ticket.ticketId}`} onClick={() => onCloseTicket(ticket.ticketId)}>
              ✓ {t("Cerrar solicitud", "Close request")}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const INBOX_FILTERS = ["new", "review", "replied", "closed", "all"];

const AdminInbox = ({ t, lang, tickets, loading, loadError, busy, onReply, onStatus, onDeleteTicket, onDeleteMessage, onClearAll, onHistory }) => {
  const [filter, setFilter] = useState("new");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [mobileShowConv, setMobileShowConv] = useState(false);

  const counts = useMemo(() => {
    const result = { all: tickets.length };
    STATUS_ORDER.forEach((status) => {
      result[status] = tickets.filter((ticket) => ticket.status === status).length;
    });
    return result;
  }, [tickets]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return tickets.filter((ticket) => {
      if (filter !== "all" && ticket.status !== filter) return false;
      if (!needle) return true;
      return (
        String(ticket.number).includes(needle.replace("#", "")) ||
        ticket.userName.toLowerCase().includes(needle) ||
        ticket.contact.toLowerCase().includes(needle) ||
        ticket.messages.some((message) => message.text.toLowerCase().includes(needle))
      );
    });
  }, [filter, search, tickets]);

  useEffect(() => {
    if (selectedId && visible.some((ticket) => ticket.ticketId === selectedId)) return;
    if (selectedId && tickets.some((ticket) => ticket.ticketId === selectedId)) return;
    setSelectedId(visible[0]?.ticketId || "");
  }, [selectedId, tickets, visible]);

  const selected = tickets.find((ticket) => ticket.ticketId === selectedId) || null;
  const filterLabel = {
    new: t("Pendientes", "Pending"),
    review: t("En revisión", "In review"),
    replied: t("Respondidas", "Answered"),
    closed: t("Cerradas", "Closed"),
    all: t("Todas", "All"),
  };

  return (
    <>
      <div className="xsu-inbox-tools">
        <span className="xsu-mute">
          {t(`${counts.new} pendientes · ${counts.all} solicitudes en total`, `${counts.new} pending · ${counts.all} requests in total`)}
        </span>
        <button type="button" className="xsu-btn is-red is-small" disabled={busy === "clear" || tickets.length === 0} onClick={onClearAll}>
          🗑 {t("Borrar todos los mensajes", "Delete all messages")}
        </button>
      </div>
      <section className={`xsu-card xsu-inbox${mobileShowConv ? " is-conv" : ""}`}>
        <div className="xsu-list">
          <div className="xsu-list-head">
            <label className="xsu-search is-small">
              <span aria-hidden="true">🔍</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("Buscar jugador, número o texto…", "Search player, number or text…")}
              />
            </label>
            <div className="xsu-tabs">
              {INBOX_FILTERS.map((key) => (
                <button type="button" key={key} className={filter === key ? "is-on" : ""} onClick={() => setFilter(key)}>
                  {filterLabel[key]}
                  {counts[key] > 0 ? ` · ${counts[key]}` : ""}
                </button>
              ))}
            </div>
          </div>
          <div className="xsu-list-body">
            {loading ? <p className="xsu-mute xsu-pad">{t("Cargando…", "Loading…")}</p> : null}
            {loadError ? <p className="xsu-error xsu-pad">{loadError}</p> : null}
            {!loading && visible.length === 0 ? (
              <p className="xsu-mute xsu-pad">{t("No hay solicitudes aquí.", "No requests here.")}</p>
            ) : null}
            {visible.map((ticket) => {
              const last = ticket.messages[ticket.messages.length - 1];
              const category = categoryOf(ticket.category);
              return (
                <button
                  type="button"
                  key={ticket.ticketId}
                  className={`xsu-item${ticket.ticketId === selectedId ? " is-on" : ""}`}
                  onClick={() => {
                    setSelectedId(ticket.ticketId);
                    setMobileShowConv(true);
                  }}
                >
                  <span className={`xsu-av ${toneOf(ticket.userId || ticket.userName)}`} aria-hidden="true">{initialOf(ticket.userName)}</span>
                  <span className="xsu-item-text">
                    <span className="xsu-item-top">
                      <b>{ticket.userName}</b>
                      <span className={`xsu-pill is-${category.tone}`}>{category.icon} {(category[lang] || category.es)[0]}</span>
                    </span>
                    <span className="xsu-item-last">
                      {last?.isAdmin ? "↩ " : ""}
                      {last?.text || ""}
                    </span>
                  </span>
                  <span className="xsu-item-side">
                    <span>{formatAgo(ticket.lastActivityAt, lang)}</span>
                    <span className="xsu-mute">#{ticket.number}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="xsu-conv">
          {selected ? (
            <>
              <div className="xsu-conv-head">
                <button type="button" className="xsu-conv-back" onClick={() => setMobileShowConv(false)}>
                  ‹ {t("Solicitudes", "Requests")}
                </button>
                <span className={`xsu-av ${toneOf(selected.userId || selected.userName)}`} aria-hidden="true">{initialOf(selected.userName)}</span>
                <div className="xsu-conv-who">
                  <div>
                    <b>{selected.userName}</b> · {t("solicitud", "request")} #{selected.number}{" "}
                    <StatusPill status={selected.status} language={lang} />
                  </div>
                  <div className="xsu-conv-tags">
                    {selected.contact ? <span className="xsu-pill is-cyan">📞 {selected.contact}</span> : null}
                    <span className="xsu-pill is-cyan">
                      {categoryOf(selected.category).icon} {(categoryOf(selected.category)[lang] || categoryOf(selected.category).es)[0]}
                    </span>
                    {contextLabel(selected, t) ? <span className="xsu-pill is-cyan">{contextLabel(selected, t)}</span> : null}
                    {selected.userId ? (
                      <button type="button" className="xsu-pill is-cyan is-button" onClick={() => onHistory(selected)}>
                        🕘 {t("Ver historial del jugador", "Player history")}
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className="xsu-conv-actions">
                  {selected.status !== "review" && selected.status !== "closed" ? (
                    <button type="button" className="xsu-btn is-small" disabled={busy === `status:${selected.ticketId}`} onClick={() => onStatus(selected.ticketId, "review")}>
                      👀 {t("En revisión", "In review")}
                    </button>
                  ) : null}
                  {selected.status !== "closed" ? (
                    <button type="button" className="xsu-btn is-small" disabled={busy === `status:${selected.ticketId}`} onClick={() => onStatus(selected.ticketId, "closed")}>
                      ✓ {t("Marcar resuelta", "Mark solved")}
                    </button>
                  ) : (
                    <button type="button" className="xsu-btn is-small" disabled={busy === `status:${selected.ticketId}`} onClick={() => onStatus(selected.ticketId, "new")}>
                      ↺ {t("Reabrir", "Reopen")}
                    </button>
                  )}
                  <button type="button" className="xsu-btn is-red is-small" disabled={busy === `delete:${selected.ticketId}`} onClick={() => onDeleteTicket(selected)}>
                    {t("Borrar", "Delete")}
                  </button>
                </div>
              </div>
              <Conversation ticket={selected} lang={lang} t={t} canDelete mineIsAdmin onDeleteMessage={onDeleteMessage} />
              <ReplyBox
                t={t}
                lang={lang}
                ticketId={selected.ticketId}
                busy={busy}
                onReply={onReply}
                quickReplies
                placeholder={t(`Responder a ${selected.userName}… (Enter envía, Mayús+Enter salta de línea)`, `Reply to ${selected.userName}… (Enter sends, Shift+Enter new line)`)}
              />
            </>
          ) : (
            <div className="xsu-conv-empty">
              <span aria-hidden="true">📭</span>
              <p>{t("Elige una solicitud de la lista.", "Pick a request from the list.")}</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Donate;
